import React, { useEffect, useState } from "react";
import { getFileURL } from "../../Helpers/backendHelper";
import { PostFileInterface } from "../../Helpers/backendResponseInterfaces";
import { CircularProgress } from "@mui/material";

interface ImageModalProps {
  imageUrl: string;
  onClose: () => void;
}

const ImageModal: React.FC<ImageModalProps> = ({ imageUrl, onClose }) => {
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.code === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscapeKey);

    return () => {
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-80 p-6 cursor-pointer z-50"
      onClick={onClose}
    >
      <div className="md:w-4/5 md:h-4/5 rounded-lg overflow-hidden relative bg-white bg-opacity-5 p-5">
        <img
          src={imageUrl}
          alt="Full Screen"
          className="w-full h-full object-contain"
          onClick={(e) => e.stopPropagation()}
        />
      </div>
    </div>
  );
};

interface ImageProps {
  fileId: number;
  currentFile?: number;
  setCurrentFile: (fileId: number) => void;
  fileList: { id: number }[];
}

const Image: React.FC<ImageProps> = ({
  fileId,
  currentFile,
  setCurrentFile,
  fileList,
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const imageURL = getFileURL(currentFile ? currentFile : fileId);

  const handleModal = () => {
    setModalOpen(!modalOpen);
  };

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  useEffect(() => {
    const handleArrowKeys = (event: KeyboardEvent) => {
      if (modalOpen) {
        if (event.key === "ArrowLeft") {
          const currentIndex = fileList.findIndex(
            (image) => image.id === currentFile,
          );
          const prevIndex =
            (currentIndex - 1 + fileList.length) % fileList.length;
          setCurrentFile(fileList[prevIndex].id);
        } else if (event.key === "ArrowRight") {
          const currentIndex = fileList.findIndex(
            (image) => image.id === currentFile,
          );
          const nextIndex = (currentIndex + 1) % fileList.length;
          setCurrentFile(fileList[nextIndex].id);
        }
      }
    };

    document.addEventListener("keydown", handleArrowKeys);

    return () => {
      document.removeEventListener("keydown", handleArrowKeys);
    };
  }, [currentFile, fileList, modalOpen, setCurrentFile]);

  return (
    <div
      className="snap-start w-full flex-shrink-0 h-full flex items-center justify-center max-h-72 cursor-pointer overflow-hidden"
      onClick={handleModal}
    >
      {!imageLoaded && (
        <div className="loader">
          <CircularProgress color="inherit" />
        </div>
      )}

      <img
        className={`rounded-lg overflow-hidden object-center ${
          imageLoaded ? "visible" : "hidden"
        }`}
        src={getFileURL(fileId)}
        alt={`${fileId}`}
        onLoad={handleImageLoad}
      />

      {modalOpen && <ImageModal imageUrl={imageURL} onClose={handleModal} />}
    </div>
  );
};

interface ImagesProps {
  images: PostFileInterface[];
}

const Images: React.FC<ImagesProps> = ({ images }) => {
  const [currentFile, setCurrentFile] = useState<number | undefined>(undefined);
  return (
    <div className="snap-x mx-auto snap-mandatory h-full flex w-full overflow-x-auto">
      {images.map((image) => (
        <Image
          key={image.id}
          fileId={image.id}
          currentFile={currentFile}
          setCurrentFile={setCurrentFile}
          fileList={images}
        />
      ))}
    </div>
  );
};

export default Images;
